import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellOff, Check, Clock, Mail, MapPin, Pencil, Sparkles, X } from "lucide-react";

import {
  fetchMatchProfile,
  previewMatchProfile,
  setDiscoverNotify,
  updateMatchProfile,
} from "../../../services/tendersApi";
import { showErrorToast } from "../../../utils/toast";
import IndustryPicker from "./IndustryPicker";
import { ProvenBadge } from "./TenderUI";

/* "Let Sensa find tenders for you" — the match profile, in three steps.
 *
 * Everything arrives pre-filled from Memory (lines of work, codes, capabilities,
 * where the company works), so setup is confirming, not typing. What is saved is
 * only what Memory cannot say: codes to add or never match, extra words, the
 * set-asides held, where the company can perform, the kinds of notice wanted.
 * The live count is a database count — editing here never costs a model call.
 *
 * Read at a glance: 15px body, 13.5px hints, each step opening with what it is
 * for, so nobody has to decode a form to know what Sensa will do with it.
 */

const STEPS = [
  {
    label: "Company",
    title: "What Sensa found in your Memory",
    intro:
      "These lines of work are what Sensa searches for; they come from your Memory, so change them there. You'll fine-tune industries and search words in the next step.",
  },
  {
    label: "What you bid on",
    title: "What you bid on",
    intro: "Everything here is pre-filled. Change only what's off — a tighter profile means better matches.",
  },
  {
    label: "Alerts",
    title: "How should Sensa tell you about new fits?",
    intro: "Pick one. You can change it any time from Tune.",
  },
];

const NOTIFY_OPTIONS = [
  { value: "bell", label: "In-app", hint: "A notification in Docusensa.", Icon: Bell, recommended: true },
  { value: "bell_email", label: "In-app + email", hint: "The same, plus an email to you.", Icon: Mail },
  { value: "off", label: "Off", hint: "No notifications. Check Discover yourself.", Icon: BellOff },
];

// What each choice actually sends, shown before it is picked — worded as
// discovery.notify words it (title, "Top: …" body, "[Docusensa]" subject).
function AlertPreview({ value }) {
  const bell = (
    <div className="flex items-start gap-3 rounded-xl border border-[#18101C]/10 bg-white px-3.5 py-3 shadow-sm">
      <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-[#A04A97]/10 text-[#A04A97]">
        <Sparkles className="h-4 w-4" strokeWidth={2} />
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold text-[#18101C]">3 new tenders fit your business</span>
        <span className="block text-[13px] text-[#77707E]">Top: the best new match · Strong fit</span>
      </span>
    </div>
  );
  if (value === "off") {
    return (
      <p className="rounded-xl border border-dashed border-[#18101C]/15 px-4 py-3 text-[14px] leading-relaxed text-[#6B6472]">
        Nothing is sent. New matches still appear in Discover, marked <b className="text-[#8E4187]">New</b>.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {bell}
      {value === "bell_email" && (
        <div className="flex items-start gap-3 rounded-xl border border-[#18101C]/10 bg-white px-3.5 py-3 shadow-sm">
          <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-[#18101C]/[0.06] text-[#52525B]">
            <Mail className="h-4 w-4" strokeWidth={2} />
          </span>
          <span className="min-w-0">
            <span className="block text-[14px] font-semibold text-[#18101C]">
              [Docusensa] 3 new tenders fit your business
            </span>
            <span className="block text-[13px] text-[#77707E]">Email · with a link to your Discover feed</span>
          </span>
        </div>
      )}
    </div>
  );
}

function AlertChoice({ value, onChange }) {
  return (
    <div>
      <div role="radiogroup" aria-label="New-match alerts" className="grid gap-3 sm:grid-cols-3">
        {NOTIFY_OPTIONS.map(({ value: v, label, hint, Icon, recommended }) => {
          const on = value === v;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(v)}
              className={`relative flex flex-col items-start rounded-2xl border p-4 text-left transition-colors ${
                on
                  ? "border-[#A04A97] bg-[#FBF2FA] shadow-[0_2px_12px_rgba(160,74,151,0.12)]"
                  : "border-[#18101C]/12 bg-white hover:border-[#A04A97]/40"
              }`}
            >
              {on && (
                <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full bg-[#A04A97] text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              )}
              <span
                className={`grid h-10 w-10 place-items-center rounded-xl ${
                  on ? "bg-[#A04A97] text-white" : "bg-[#18101C]/[0.05] text-[#52525B]"
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <span className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="text-[15.5px] font-semibold text-[#18101C]">{label}</span>
                {recommended && (
                  <span className="rounded-full bg-[#A04A97]/10 px-1.5 py-[1px] text-[11px] font-semibold text-[#8E4187]">
                    Recommended
                  </span>
                )}
              </span>
              <span className="mt-1 text-[13.5px] leading-snug text-[#6B6472]">{hint}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-[#18101C]/[0.03] px-4 py-3">
        <Clock className="mt-0.5 h-4 w-4 flex-none text-[#6B6472]" strokeWidth={2.2} />
        <p className="text-[14px] leading-relaxed text-[#52525B]">
          <b className="font-semibold text-[#18101C]">At most once a day</b>, after the daily tender update — and only
          when Strong or Good fits appear. No new fits, no message.
        </p>
      </div>

      <p className="mb-2 mt-5 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#77707E]">What you'll get</p>
      <AlertPreview value={value} />
    </div>
  );
}

const chipBase =
  "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[14px] leading-tight transition-colors";

function Chip({ children, onRemove, tone = "brand" }) {
  const tones = {
    brand: "border-[#E7CDE4] bg-[#FBF2FA] text-[#7A3A73]",
    plain: "border-[#18101C]/12 bg-white text-[#3F3F46]",
    muted: "border-dashed border-[#18101C]/15 bg-[#18101C]/[0.02] text-[#77707E]",
  };
  return (
    <span className={`${chipBase} ${tones[tone]}`}>
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove"
          className="-mr-1 grid h-5 w-5 place-items-center rounded text-current opacity-60 hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.6} />
        </button>
      )}
    </span>
  );
}

function Toggle({ on, children, onClick, locked = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`${chipBase} ${
        on
          ? "border-[#A04A97] bg-[#A04A97] text-white"
          : "border-[#18101C]/15 bg-white text-[#3F3F46] hover:border-[#A04A97]/50"
      } ${locked ? "cursor-default" : ""}`}
    >
      {on && <Check className="h-3.5 w-3.5" strokeWidth={2.8} />}
      {children}
    </button>
  );
}

function AddInput({ placeholder, onAdd, transform = (v) => v }) {
  const [value, setValue] = useState("");
  const commit = () => {
    const v = transform(value.trim());
    if (v) onAdd(v);
    setValue("");
  };
  return (
    <input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === ",") {
          e.preventDefault();
          commit();
        }
      }}
      onBlur={commit}
      placeholder={placeholder}
      className="h-9 min-w-[170px] flex-1 rounded-lg border border-dashed border-[#18101C]/20 bg-white px-3 text-[14px] text-[#18101C] outline-none placeholder:text-[#9CA3AF] focus:border-[#A04A97]"
    />
  );
}

function Field({ label, hint, action, children }) {
  return (
    <section className="mb-7">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-[#18101C]">{label}</h3>
        {action}
      </div>
      {hint && <p className="mt-0.5 text-[13.5px] leading-relaxed text-[#6B6472]">{hint}</p>}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">{children}</div>
    </section>
  );
}

function Stepper({ step, onStep }) {
  return (
    <ol className="mt-5 flex items-center gap-2">
      {STEPS.map((s, i) => {
        const done = i < step;
        const current = i === step;
        return (
          <li key={s.label} className={`flex items-center gap-2 ${i < STEPS.length - 1 ? "flex-1" : ""}`}>
            <button type="button" onClick={() => onStep(i)} className="flex flex-none items-center gap-2">
              <span
                className={`grid h-7 w-7 place-items-center rounded-full text-[13px] font-semibold ${
                  done || current ? "bg-[#A04A97] text-white" : "border border-[#18101C]/20 text-[#77707E]"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
              </span>
              <span className={`text-[14px] ${current ? "font-semibold text-[#18101C]" : "text-[#6B6472]"}`}>
                {s.label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <span className={`h-px flex-1 ${done ? "bg-[#A04A97]" : "bg-[#18101C]/12"}`} aria-hidden />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function LineOfWork({ line }) {
  return (
    <div className="rounded-xl border border-[#18101C]/10 bg-white px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-auto text-[15.5px] font-semibold text-[#18101C]">{line.label}</p>
        {line.on_site && (
          <span
            title="Done in person at the buyer's site: only tenders in your locations are shown"
            className="inline-flex items-center gap-1 rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2 py-[2px] text-[11.5px] font-semibold text-[#1D4ED8]"
          >
            <MapPin className="h-3 w-3" strokeWidth={2.4} />
            On site
          </span>
        )}
        <ProvenBadge proof={line.proof} />
      </div>
      {!!line.terms?.length && (
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-[#52525B]">
          <span className="text-[#77707E]">Searches for </span>
          {line.terms.slice(0, 6).join(" · ")}
        </p>
      )}
      {!!line.proof.length && (
        <p className="mt-1 text-[13.5px] leading-relaxed text-[#52525B]">
          <span className="text-[#77707E]">Proven by </span>
          {line.proof.join(" · ")}
        </p>
      )}
    </div>
  );
}

const without = (list, v) => list.filter((x) => x !== v);
const withOne = (list, v) => (list.includes(v) ? list : [...list, v]);

export default function MatchSetupModal({ open, startStep = 0, notify = "bell", onClose, onSaved }) {
  const [step, setStep] = useState(startStep);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [notifyChoice, setNotifyChoice] = useState(notify);
  const [count, setCount] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(startStep);
    setNotifyChoice(notify);
    setProfile(null);
    fetchMatchProfile()
      .then((p) => {
        setProfile(p);
        setDraft({
          include_codes: p.include_codes.map((c) => c.code),
          skip_codes: p.skip_codes,
          exclude_codes: p.exclude_codes.map((c) => c.code),
          extra_terms: p.extra_terms,
          not_our_work: p.your_not_our_work,
          set_asides: p.set_asides,
          states: p.states,
          notice_types: p.notice_types,
        });
      })
      .catch(() => showErrorToast("Couldn't load your match profile. Please try again."));
  }, [open, startStep, notify]);

  // The live count, on a short debounce so typing a code does not fire a count per key.
  const draftKey = JSON.stringify(draft && {
    include_codes: draft.include_codes, skip_codes: draft.skip_codes,
    exclude_codes: draft.exclude_codes, extra_terms: draft.extra_terms,
  });
  useEffect(() => {
    if (!open || !draft) return undefined;
    const controller = new AbortController();
    const t = setTimeout(() => {
      previewMatchProfile(JSON.parse(draftKey), { signal: controller.signal })
        .then((r) => setCount(r))
        .catch(() => {});
    }, 450);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftKey]);

  if (!open) return null;

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  // A certification is on when Memory records it (fixed) or every code it covers is confirmed here.
  const certOn = (cert) => cert.in_memory || cert.codes.every((c) => draft.set_asides.includes(c));
  const toggleCert = (cert) =>
    set(
      "set_asides",
      certOn(cert)
        ? draft.set_asides.filter((c) => !cert.codes.includes(c))
        : [...new Set([...draft.set_asides, ...cert.codes])]
    );

  const finish = async () => {
    setSaving(true);
    try {
      const saved = await updateMatchProfile({ ...draft, setup_done: true });
      if (notifyChoice !== notify) await setDiscoverNotify(notifyChoice);
      onSaved?.(saved, notifyChoice);
    } catch {
      showErrorToast("Couldn't save your match profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const here = STEPS[step];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#18101C]/40 p-4 font-outfit">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Let Sensa find tenders for you"
        className="flex max-h-[92vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex-none border-b border-[#18101C]/[0.07] px-7 pb-5 pt-6">
          <div className="flex items-start gap-3.5">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-[#A04A97]/10 text-[#A04A97]">
              <Sparkles className="h-5 w-5" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-[22px] font-semibold leading-tight text-[#18101C]">Let Sensa find tenders for you</h2>
              <p className="mt-1 text-[14.5px] text-[#6B6472]">About a minute. You can change all of this later.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-9 w-9 place-items-center rounded-lg text-[#6B6472] hover:bg-[#18101C]/[0.06]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <Stepper step={step} onStep={setStep} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
          {!profile || !draft ? (
            <div className="space-y-3 py-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-5 animate-pulse rounded bg-[#18101C]/[0.06]" style={{ width: `${80 - i * 15}%` }} />
              ))}
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h3 className="text-[18px] font-semibold text-[#18101C]">{here.title}</h3>
                <p className="mt-1 text-[14.5px] leading-relaxed text-[#6B6472]">{here.intro}</p>
              </div>

              {step === 0 ? (
                <>
                  {!profile.memory_ready && (
                    <p className="mb-6 rounded-xl border border-[#F5C46B]/60 bg-[#FFF8E6] px-4 py-3 text-[14px] leading-relaxed text-[#92400E]">
                      Memory is empty. Upload company documents to the Knowledge Base, or add industry codes on the
                      next step to start matching by code alone.
                    </p>
                  )}
                  <Field label="Lines of work">
                    {profile.lines_of_work.length ? (
                      <div className="w-full space-y-2.5">
                        {profile.lines_of_work.map((line) => (
                          <LineOfWork key={line.label} line={line} />
                        ))}
                      </div>
                    ) : (
                      <span className="text-[14px] text-[#77707E]">Worked out on the first match run.</span>
                    )}
                  </Field>
                  <Field
                    label="What you're great at"
                    action={
                      <Link
                        to="/rfp-knowledgeBase/Memory"
                        className="inline-flex flex-none items-center gap-1 text-[13.5px] font-semibold text-[#A04A97] hover:underline"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={2.4} />
                        Edit in Memory
                      </Link>
                    }
                  >
                    {profile.capabilities.slice(0, 14).map((c) => (
                      <Chip key={c} tone="plain">{c}</Chip>
                    ))}
                  </Field>
                </>
              ) : step === 1 ? (
                <>
                  <IndustryPicker
                    profile={profile}
                    draft={draft}
                    onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
                  />
                  <Field label="Extra search words" hint="Words agencies use for your work that Memory doesn't.">
                    {draft.extra_terms.map((w) => (
                      <Chip key={w} onRemove={() => set("extra_terms", without(draft.extra_terms, w))}>{w}</Chip>
                    ))}
                    <AddInput placeholder="+ Add a word" onAdd={(v) => set("extra_terms", withOne(draft.extra_terms, v))} />
                  </Field>
                  <Field
                    label="Certifications you hold"
                    hint="Many federal tenders are reserved for certified businesses. Tick the ones you hold and Sensa shows you the tenders reserved for them."
                  >
                    {profile.certifications.map((cert) => (
                      <Toggle
                        key={cert.key}
                        on={certOn(cert)}
                        locked={cert.in_memory}
                        onClick={() => !cert.in_memory && toggleCert(cert)}
                      >
                        {cert.label}
                        {cert.in_memory && <span className="text-[12px] font-normal opacity-80">· in Memory</span>}
                      </Toggle>
                    ))}
                  </Field>
                  <Field
                    label="Where you can perform"
                    hint="Two-letter states. Work done on site elsewhere is not shown; work you can deliver remotely is shown wherever it is. Empty means anywhere."
                  >
                    {draft.states.map((s) => (
                      <Chip key={s} onRemove={() => set("states", without(draft.states, s))}>{s}</Chip>
                    ))}
                    <AddInput
                      placeholder="+ State (e.g. MD)"
                      transform={(v) => (/^[a-z]{2}$/i.test(v) ? v.toUpperCase() : "")}
                      onAdd={(v) => set("states", withOne(draft.states, v))}
                    />
                  </Field>
                  <Field label="Kinds of notice" hint="None selected means all of them except award notices.">
                    {profile.notice_type_options.map((o) => (
                      <Toggle
                        key={o.value}
                        on={draft.notice_types.includes(o.value)}
                        onClick={() =>
                          set("notice_types", draft.notice_types.includes(o.value)
                            ? without(draft.notice_types, o.value) : [...draft.notice_types, o.value])
                        }
                      >
                        {o.label}
                      </Toggle>
                    ))}
                  </Field>
                </>
              ) : (
                <AlertChoice value={notifyChoice} onChange={setNotifyChoice} />
              )}
            </>
          )}
        </div>

        <div className="flex flex-none items-center justify-between gap-4 border-t border-[#18101C]/[0.07] bg-[#FCFBFC] px-7 py-4">
          {/* How wide the search is, not how many will be shown: a match count
              here read as "596 tenders fit you" when Sensa reads the closest
              few and shows only those that do. */}
          <div className="min-w-0">
            <p className="text-[14px] text-[#52525B]">
              Open tenders using your words or codes:{" "}
              <span className="font-semibold text-[#18101C]">
                {count ? `≈ ${count.count.toLocaleString()}${count.capped ? "+" : ""}` : "…"}
              </span>
            </p>
            <p className="mt-0.5 text-[13px] leading-snug text-[#77707E]">
              Sensa reads the closest of these and shows only the ones that fit.
            </p>
          </div>
          <div className="flex flex-none gap-2.5">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="h-11 rounded-xl border border-[#18101C]/15 bg-white px-5 text-[15px] font-semibold text-[#52525B] hover:bg-[#18101C]/[0.04]"
              >
                Back
              </button>
            )}
            <button
              type="button"
              disabled={!draft || saving}
              onClick={() => (step < STEPS.length - 1 ? setStep((s) => s + 1) : finish())}
              className="h-11 rounded-xl bg-[#A04A97] px-6 text-[15px] font-semibold text-white hover:bg-[#8E4187] disabled:opacity-60"
            >
              {step < STEPS.length - 1 ? "Continue" : saving ? "Saving…" : "Start matching"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
